import apiClient from "../../services/ApiClientService";
import { CustomerModel } from "../models/Customer";
import { CustomerAttributeConstraint } from "../models/CustomerAttributeConstraint";
import { Utilities } from "../utils/utilities";
import { DateTimeFormatStrings } from "../enums";
import { CustomerDeterminant } from "../models/CustomerDeterminant";
import { CustomerMinimal } from "../models/CustomerMinimal";
import { CustomerUpdateRunDetail } from "../models/CustomerUpdateRunDetail";
import { dataLoading } from "../decorators/LoadingAction.decorator";
import { cellValueFormatForUpdateColumn } from "../business/ReviewCustomers";

export class ReviewCustomersApi {
  @dataLoading(undefined)
  static async getCustomers() {
    var result = await apiClient.get("/api/Customer");
    if (result.status === 200) {
      const transformedData = this.transformToCustomerModel(result.data);
      return transformedData as CustomerModel[];
    } else {
      return undefined;
    }
  }

  @dataLoading("")
  static async exportCustomerList() {
    var response = await apiClient.get(`/api/customer/dumpCustomerList`);
    return response ? Utilities.blobFileDownload(response) : "";
  }

  @dataLoading("")
  static async getAttachment(id: string) {
    var response = await apiClient.get(`/api/customer/GetAttachment?id=${id}`);
    return response ? Utilities.blobFileDownload(response) : "";
  }

  @dataLoading(false)
  static async updateCustomer(customer: CustomerModel) {
    const response = await apiClient.post("/api/Customer", customer);
    return response.status === 200;
  }

  @dataLoading(undefined)
  static async addComment(text: string, type: string, shipTo: number, file: File | null) {
    try {
      const formData = new FormData();

      // Append JSON data as string
      formData.append(
        "data",
        JSON.stringify({
          shipto: shipTo,
          text: text,
          type: type,
        })
      );

      // Append file if exists
      if (file) {
        formData.append("file", file);
      }

      return await apiClient.post("/api/Customer/AddComment", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error("Error posting comment:", error);
    }
  }

  @dataLoading(undefined)
  static async getCustomerDeterminants(shipto: string) {
    var asOf = encodeURIComponent(Utilities.formatDate(new Date(), DateTimeFormatStrings.FORMAT_9));
    var result = await apiClient.get(`/api/customer/customerForCalculation?customerId=&shipTo=${shipto}&asOf=${asOf}`);
    if (result.status === 200) {
      return result.data as CustomerDeterminant;
    } else {
      return undefined;
    }
  }

  @dataLoading([])
  static async getCustomerUpdateRuns() {
    var result = await apiClient.get(`/api/customer/customerUpdateRuns`);
    if (result.status === 200) {
      return Utilities.convertObjectToNameValuePair(result.data, []);
    } else {
      return [];
    }
  }

  @dataLoading(undefined)
  static async getCustomerUpdateRunDetails(runid: string) {
    var result = await apiClient.get(`/api/customer/customerUpdateRunDetails?runid=${runid}`);
    if (result.status === 200) {
      var detail = result.data as CustomerUpdateRunDetail;
      if (detail && detail.history) {
        detail.history.forEach((item) => (item.createdAt = Utilities.formatDate(new Date(item.createdAt), DateTimeFormatStrings.FORMAT_12)));
      }
      return detail;
    } else {
      return undefined;
    }
  }

  @dataLoading("")
  static async dumpSurchargeEventCalculationResults(runid: string) {
    var response = await apiClient.get(`/api/customer/dumpSurchargeEventCalculationResults?runid=${runid}`);
    return response ? Utilities.blobFileDownload(response) : "";
  }

  static async getCustomerAttributeConstraints() {
    var result = await apiClient.get("/api/Customer/GetCustomerAttributeConstraints");
    if (result.status === 200) {
      var returnData = result.data as CustomerAttributeConstraint[];
      returnData.forEach((item) => (item.displayValue = `${item.code}-${item.description}`));
      return returnData;
    } else {
      return [];
    }
  }

  static async shortListCustomers(query: string) {
    try {
      var result = await apiClient.get(`/api/customer/shortListCustomers?namePattern=${query}`);
      if (result.status === 200) {
        return result.data as CustomerMinimal[];
      } else {
        return [];
      }
    } catch (error) {
      console.log(error);
    }

    return [];
  }

  private static transformToCustomerModel(data: any[]): CustomerModel[] {
    return data.map((item): CustomerModel => {
      const determinantsParsed = JSON.parse(item.determinants);
      const determinantsArray = (determinantsParsed && determinantsParsed.Determinants) ?? determinantsParsed.Determinants;
      const determinantsProps: { [key: string]: any } = {};

      determinantsArray.forEach((det: any) => {
        const key = det.Type;
        determinantsProps[key] = det;
      });

      return {
        ShipTo: item.shipTo as number,
        Name: item.name as string,
        SurchargeType: item.surchargeType as string,
        IncludeInNewSurchargeEvents: item.includeInNewSurchargeEvents as boolean,
        PrimarySalesRep: item.primarySalesRep as string,
        Formula: item.formulaType as string,
        Plant: item.plant as string,
        Region: item.region as string,
        ProductType: item.productType as string,
        Frequency: item.frequency as string,
        Currency: item.currency as string,
        Multiplier: item.multiplier as number,
        Update: item.update as string,
        UpdateEx: cellValueFormatForUpdateColumn(item.update),
        SalesOrg: item.salesOrg as string,
        SAPMaterialProductCode: item.sapMaterialProductCode as string,
        SAPEnergySurchargeCode: item.sapEnergySurchargeCode as string,
        Unit: item.unit as number,
        Uom: item.uom as string,
        Determinants: item.determinants as string,
        SurchargeStartDate: item.surchargeStartDate ? new Date(item.surchargeStartDate) : null,
        Create: false,
        SendEmail: item.sendEmail as boolean,
        LatestNoteText: item.latestNoteText as string,
        DeterminantsProps: determinantsProps,
        UpdatedBy: item.updatedBy as string,
        UpdatedAt: item.updatedAt ? new Date(item.updatedAt) : null,
      };
      // Map other properties as needed
    });
  }
}
